import { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChange, getUserData, ensureUserDocument } from '../services/auth';

const AuthContext = createContext({
  user: null,
  loading: true,
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    let unsubscribe = null;

    // Always resolve after 3 seconds max to prevent infinite loading
    const timeoutId = setTimeout(() => {
      if (mounted) {
        setLoading(false);
      }
    }, 3000);

    const initializeAuth = async () => {
      try {
        unsubscribe = onAuthStateChange(async (firebaseUser) => {
          if (!mounted) return;

          try {
            if (firebaseUser) {
              let userData = await getUserData(firebaseUser.uid);
              // Phone (and other) sign-ins may not have a Firestore doc yet
              if (!userData) {
                await ensureUserDocument({
                  displayName: firebaseUser.displayName || '',
                  email: firebaseUser.email || '',
                  phone: firebaseUser.phoneNumber || '',
                });
                userData = await getUserData(firebaseUser.uid);
              }
              if (mounted) {
                setUser(userData);
              }
            } else if (mounted) {
              setUser(null);
            }
          } catch (error) {
            console.error('[AUTH] Error loading user data:', error);
            if (mounted) {
              setUser(null);
            }
          } finally {
            if (mounted) {
              clearTimeout(timeoutId);
              setLoading(false);
            }
          }
        });
      } catch (error) {
        console.error('[AUTH] Error initializing auth:', error);
        if (mounted) {
          setLoading(false);
          clearTimeout(timeoutId);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  );
};
